import { debounce } from 'throttle-debounce';

import { $waitForClient } from '@client/core/atoms/config.js';
import { addLifecycleEvent } from '@client/core/atoms/metrics.js';
import { debouncedSendMetrics } from '@client/core/services/metrics.js';
import {
  AdPlacement,
  debugLog,
  type DisplayPlacementId,
  type FinnLegendaryDisplayPlacementId
} from '@schibsted-nmp/advertising-shared';
import { setupAdnEventListeners } from '@client/adnuntius/AdUnit/setupAdnEventListeners.js';

import { Api, type Common } from './Api/index.js';

let pendingSlots = new Array<Common.AdUnit>();
let delayedPendingSlots = new Array<Common.AdUnit>();

/**
 * Delay left 1 and right 1 with 200ms, because when sent as batch request with
 * top 1 it does not trigger adnami topscroll regularly.
 */
const delayedBatchPlacements = new Array<
  DisplayPlacementId | FinnLegendaryDisplayPlacementId
>(AdPlacement.AdvtLeft1.id, AdPlacement.AdvtRight1.id);

let subscribedToClient = false;

async function loadBatchedAllSlots(
  adUnits: Array<Common.AdUnit>,
  ...abortSignals: ReadonlyArray<AbortSignal>
) {
  await Api(...abortSignals).request({ adUnits });
  await setupAdnEventListeners(adUnits, ...abortSignals);
}

/** Debounce function that accumulates slots and refreshes them after 200ms. */
const debounceLoadAd = debounce(
  200,
  () => {
    const slotsToLoad = [...pendingSlots];
    pendingSlots = [];

    loadBatchedAllSlots(slotsToLoad)
      .then(() => {
        addLifecycleEvent(
          `ADN Load batch of Ads for ${slotsToLoad.map((au) => au.targetId).join(', ')}`
        );

        debouncedSendMetrics();
      })
      .catch((error: unknown) => {
        debugLog(
          `Failed ADN Load batch of Ads for ${slotsToLoad.map((au) => au.targetId).join(', ')}`,
          error
        );
      });
  },
  { atBegin: false }
);

/** Debounce function that accumulates slots and refreshes them after 400ms. */
const delayedDebounceLoadAd = debounce(
  400,
  () => {
    const slotsToLoad = [...delayedPendingSlots];
    delayedPendingSlots = [];

    loadBatchedAllSlots(slotsToLoad)
      .then(() => {
        addLifecycleEvent(
          `ADN Load batch of Ads for ${slotsToLoad.map((au) => au.targetId).join(', ')}`
        );

        debouncedSendMetrics();
      })
      .catch((error: unknown) => {
        debugLog(
          `Failed ADN Load batch of Ads for ${slotsToLoad.map((au) => au.targetId).join(', ')}`,
          error
        );
      });
  },
  { atBegin: false }
);

export function debouncedLoadAstTags(adUnit: Common.AdUnit) {
  const shouldDelay = delayedBatchPlacements.includes(adUnit.targetId);

  if (shouldDelay) {
    delayedPendingSlots.push(adUnit);
  } else {
    pendingSlots.push(adUnit);
  }

  const triggerDebounce = () => {
    if (shouldDelay) delayedDebounceLoadAd();
    else debounceLoadAd();
  };

  const waitForClient = $waitForClient.get();

  if (waitForClient && !subscribedToClient) {
    subscribedToClient = true;

    $waitForClient.subscribe((wait) => {
      if (!wait) triggerDebounce();
    });
  } else if (!waitForClient) {
    triggerDebounce();
  }
}
